import { useState } from 'react'
import { X, Book, Search, ExternalLink, Info } from 'lucide-react'
import availableLibraries from '../config/available-libraries.json'

/**
 * Library Reference Viewer
 * Shows what libraries the AI knows about (for user reference only)
 * AI automatically knows these via grounding system
 */
export default function LibraryReference({ isOpen, onClose }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')

  if (!isOpen) return null

  const categories = ['all', ...Object.keys(availableLibraries.categories)]

  const getFilteredLibraries = () => {
    const results = []
    
    Object.entries(availableLibraries.categories).forEach(([category, data]) => {
      if (selectedCategory !== 'all' && selectedCategory !== category) return

      if (data.preferred) {
        data.preferred.forEach(lib => {
          if (matchesSearch(lib)) {
            results.push({ ...lib, category, preference: 'PREFERRED' })
          }
        })
      }

      if (data.allowed) {
        data.allowed.forEach(lib => {
          if (matchesSearch(lib)) {
            results.push({ ...lib, category, preference: 'ALLOWED' })
          }
        })
      }
    })

    return results
  }

  const matchesSearch = (lib) => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    return (
      lib.name.toLowerCase().includes(term) ||
      lib.description?.toLowerCase().includes(term) ||
      lib.tags?.some(tag => tag.toLowerCase().includes(term))
    )
  }

  const formatCategory = (cat) => {
    return cat.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  const libraries = getFilteredLibraries()

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Book className="w-6 h-6 text-blue-400" />
              AI Library Knowledge
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              These libraries are automatically available to the AI assistant
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        {/* Info Banner */}
        <div className="p-4 bg-blue-500/10 border-b border-blue-500/30">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-300">
              <p><strong className="text-white">How it works:</strong> The AI automatically knows about these libraries. 
              When you ask it to add functionality, it will suggest appropriate libraries and add the correct 
              script tags to your index.html.</p>
              <p className="mt-2 text-xs text-gray-400">
                Example: "Add 3D graphics" → AI suggests three.js and adds: 
                <code className="ml-1 px-2 py-0.5 bg-gray-800 rounded text-blue-300">
                  &lt;script src="https://cdnjs.cloudflare.com/ajax/libs/three.js/..."&gt;
                </code>
              </p>
            </div>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="p-4 border-b border-gray-700 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              placeholder="Search libraries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Category Filter */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === cat
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {formatCategory(cat)}
              </button>
            ))}
          </div>

          {/* Stats */}
          <div className="text-sm text-gray-400">
            Showing {libraries.length} {libraries.length === 1 ? 'library' : 'libraries'}
          </div>
        </div>

        {/* Library List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {libraries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No libraries found</p>
            </div>
          ) : (
            libraries.map((lib, index) => (
              <div
                key={`${lib.name}-${index}`}
                className="bg-gray-800 rounded-lg p-4 hover:bg-gray-750 transition-colors border border-gray-700"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-white truncate">
                        {lib.name}
                      </h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        lib.preference === 'PREFERRED'
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      }`}>
                        {lib.preference}
                      </span>
                      {lib.version && (
                        <span className="px-2 py-0.5 text-xs font-mono bg-gray-700 text-gray-300 rounded">
                          v{lib.version}
                        </span>
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-sm text-gray-300 mb-3">
                      {lib.description}
                    </p>

                    {/* Reason (Why Preferred/Allowed) */}
                    {lib.reason && (
                      <div className="mb-3 p-2 bg-gray-900 rounded border border-gray-700">
                        <p className="text-xs text-gray-400">
                          <strong className="text-blue-400">Why:</strong> {lib.reason}
                        </p>
                      </div>
                    )}

                    {/* CDN URL */}
                    {lib.cdn && lib.cdn !== 'NOT_ON_CDNJS' && (
                      <div className="mb-2">
                        <code className="block text-xs bg-gray-900 p-2 rounded border border-gray-700 text-blue-300 font-mono truncate">
                          {lib.cdn}
                        </code>
                      </div>
                    )}

                    {/* Special Note */}
                    {lib.cdn === 'NOT_ON_CDNJS' && lib.note && (
                      <div className="mb-2 p-2 bg-orange-500/10 border border-orange-500/30 rounded">
                        <p className="text-xs text-orange-300">
                          ⚠️ {lib.note}
                        </p>
                      </div>
                    )}

                    {/* Tags */}
                    {lib.tags && lib.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {lib.tags.slice(0, 5).map((tag, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 text-xs bg-gray-700 text-gray-300 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Category */}
                    <div className="text-xs text-gray-500">
                      Category: {formatCategory(lib.category)}
                    </div>
                  </div>

                  {/* External Links */}
                  <div className="flex flex-col gap-2">
                    {lib.documentation && (
                      <a
                        href={lib.documentation}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                        title="Documentation"
                      >
                        <ExternalLink className="w-4 h-4 text-gray-300" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 bg-gray-800/50">
          <div className="flex items-center justify-between text-sm">
            <div className="text-gray-400">
              <strong className="text-white">Total:</strong>{' '}
              {Object.entries(availableLibraries.categories).reduce((sum, [, data]) => 
                sum + (data.preferred?.length || 0) + (data.allowed?.length || 0), 0
              )} libraries across {Object.keys(availableLibraries.categories).length} categories
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
